<template>
  <div class="__inativacao-lote">
    <h2 class="__inativacao-lote__status">
      Status do processamento:
      <span class="status-running" v-if="is('running')">
        Em Andamento: ({{ statusModel.totalRegistrosProcessados }}/{{
          statusModel.totalRegistros
        }})
      </span>
      <span class="status-finished" v-else-if="is('finished')">Finalizado</span>
      <span class="status-error" v-else-if="is('error')">
        Finalizado com Erro
      </span>
      <span class="status-idle" v-else>
        {{ is("preview") ? "Prévia gerada, aguardando confirmação" : "Carregando..." }}
      </span>
    </h2>

    <div v-if="USER_HAS_CC_PERMISSION">
      <overlay-loader v-if="loading" />

      <template v-if="is('idle', 'finished', 'error')">
        <input-upload-field
          :modelProp="fieldsModel"
          ref="inputUploadField"
          :disabled="loading"
          :fileType="['.xls', '.xlsx']"
          :getLayoutFile="getLayoutFile"
          layoutFileName="Template Inativação em Lote.xlsx"
        />
        <div class="col col-lg-auto ms-auto">
          <button
            class="btn btn-secondary"
            @click="cleanFields"
            :disabled="loading"
          >
            Limpar
          </button>
          <button
            class="btn btn-primary ms-3"
            @click="gerarPreview"
            :disabled="!IS_FILE_ATTACHED || loading"
          >
            Gerar Prévia
          </button>
        </div>
      </template>

      <template v-else-if="is('preview')">
        <div class="__inativacao-lote__preview-summary">
          <span>{{ preview.totalItens }} item(ns) na prévia</span>
          <span v-if="preview.totalErros > 0" class="text-erro">
            {{ preview.totalErros }} com erro (não serão processados)
          </span>
          <span v-if="preview.totalAlertas > 0" class="text-alerta">
            {{ preview.totalAlertas }} com alerta (revise antes de confirmar)
          </span>
        </div>
        <table-inativacao-lote-preview :preview="preview" />
        <div class="col col-lg-auto ms-auto">
          <button
            class="btn btn-secondary"
            @click="cancelarPreview"
            :disabled="loading"
          >
            Cancelar / Novo Upload
          </button>
          <button
            class="btn btn-primary ms-3"
            @click="confirmarLote"
            :disabled="!preview.podeConfirmar || loading"
          >
            Confirmar
          </button>
        </div>
      </template>
    </div>

    <h2 class="__inativacao-lote__status" v-if="is('running', 'finished', 'error')">
      <template v-if="is('running')">
        Itens do Processamento Atual
      </template>
      <template v-else>
        Itens do Último Processamento ({{
          is("error") ? "Finalizado com Erro" : "Finalizado"
        }}
        às {{ HORARIO_FIM }})
      </template>
    </h2>
    <table-inativacao-lote-status
      v-if="is('running', 'finished', 'error')"
      :itens="statusModel.itens"
    />
  </div>
</template>

<script>
import InputUploadField from "@/components/forms/fields/empresa/InputUploadField.vue";
import TableInativacaoLotePreview from "@/components/tables/TableInativacaoLotePreview.vue";
import TableInativacaoLoteStatus from "@/components/tables/TableInativacaoLoteStatus.vue";
import { CONFIRMATION, HTTP_RESPONSE } from "@/constants/strings";
import InativacaoLoteStatusModel from "@/models/empresa/InativacaoLoteStatusModel";
import InativacaoLotePreviewModel from "@/models/empresa/InativacaoLotePreviewModel";
import EmpresaInativacaoLoteService from "@/services/portal/empresa/EmpresaInativacaoLoteService";
import { formIsValid } from "@/utils/validators";

const MAX_FALHAS_CONSECUTIVAS_POLLING = 3;

export default {
  data() {
    return {
      fieldsModel: { arquivo: null },
      preview: new InativacaoLotePreviewModel(),
      statusModel: new InativacaoLoteStatusModel(),
      status: null,
      intervalo: null,
      loading: false,
      falhasConsecutivasPolling: 0,
    };
  },
  mounted() {
    this.getStatusLote();
  },
  methods: {
    async getStatusLote() {
      try {
        const { data } = await EmpresaInativacaoLoteService.getStatus();
        this.statusModel = data;
        this.falhasConsecutivasPolling = 0;
      } catch (error) {
        // Achado de revisão (IMPORTANTE): parar o polling na primeira falha
        // interrompe o acompanhamento de um lote que pode só ter sofrido uma
        // instabilidade passageira de rede. Só desistimos (e avisamos uma
        // única vez) após MAX_FALHAS_CONSECUTIVAS_POLLING falhas em sequência.
        this.falhasConsecutivasPolling += 1;
        if (
          !this.is("running") ||
          this.falhasConsecutivasPolling >= MAX_FALHAS_CONSECUTIVAS_POLLING
        ) {
          this.mxHandleRequestError(error);
          this.status = "idle";
        }
        return;
      }
      if (this.statusModel.finalizadoComErro) this.status = "error";
      else if (this.statusModel.finalizado) this.status = "finished";
      else if (this.statusModel.processando) this.status = "running";
      else this.status = "idle";
    },
    async gerarPreview() {
      if (this.loading) return;
      if (!formIsValid(this)) return;

      this.loading = true;

      const formData = new FormData();
      formData.append(
        "file",
        this.fieldsModel.arquivo,
        this.fieldsModel.arquivo.name
      );
      try {
        const { data } = await EmpresaInativacaoLoteService.preview(formData);
        this.preview = data;
        this.status = "preview";
        this.mxToggleToast(HTTP_RESPONSE.INATIVACAO_LOTE.SUCCESS.CREATE);
      } catch (error) {
        if (error?.response?.data?.messages?.length > 1) {
          this.mxToggleErrorModalAlerts(error.response.data.messages);
        } else {
          this.mxHandleRequestError(error);
        }
      }
      this.loading = false;
    },
    confirmarLote() {
      if (this.loading || !this.preview.podeConfirmar) return;

      this.mxToggleWarningModal(CONFIRMATION.INATIVACAO_LOTE.CONFIRM)
        .then(async () => {
          this.loading = true;
          try {
            await EmpresaInativacaoLoteService.confirmar({
              previewToken: this.preview.previewToken,
            });
            this.mxToggleToast(HTTP_RESPONSE.INATIVACAO_LOTE.SUCCESS.CONFIRM);
            this.fieldsModel.arquivo = null;
            this.preview = new InativacaoLotePreviewModel();
            this.getStatusLote();
          } catch (error) {
            this.mxHandleRequestError(error);
          }
          this.loading = false;
        })
        .catch(() => {});
    },
    cancelarPreview() {
      this.preview = new InativacaoLotePreviewModel();
      this.fieldsModel.arquivo = null;
      this.status = "idle";
    },
    cleanFields() {
      this.fieldsModel.arquivo = null;
    },
    is(...status) {
      return status.includes(this.status);
    },
    async getLayoutFile() {
      try {
        const { data } = await EmpresaInativacaoLoteService.getTemplateExcel();
        return data;
      } catch (error) {
        this.mxHandleRequestError(error);
      }
    },
  },
  computed: {
    IS_FILE_ATTACHED() {
      return this.fieldsModel.arquivo !== null;
    },
    USER_HAS_CC_PERMISSION() {
      return this.$can("CENTRAL_CADASTROS_INATIVAR_LOTE");
    },
    HORARIO_FIM() {
      if (!this.statusModel.dataFim) return "HH:mm de DD/MM/AAAA";
      const date = new Date(this.statusModel.dataFim);
      return `${date.getHours().toString().padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")} de ${date.toLocaleDateString()}`;
    },
  },
  watch: {
    status() {
      if (this.is("running")) {
        if (!this.intervalo) {
          this.intervalo = setInterval(this.getStatusLote, 5000);
        }
      } else {
        if (this.intervalo) clearInterval(this.intervalo);
        this.intervalo = null;
      }
    },
  },
  destroyed() {
    if (this.intervalo) clearInterval(this.intervalo);
  },
  components: {
    InputUploadField,
    TableInativacaoLotePreview,
    TableInativacaoLoteStatus,
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/style.scss";
@import "@/assets/style/colors.scss";

.__inativacao-lote {
  padding: 24px !important;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  &__status {
    font-size: 1rem;
    margin: 0;
    font-weight: normal;
    > span {
      font-weight: 500;
      font-size: 1.2rem;
      &.status-running {
        color: $status--yellow;
      }
      &.status-finished {
        color: $status--green;
      }
      &.status-error {
        color: $status--red;
      }
      &.status-idle {
        color: $secondary--gray;
      }
    }
  }
  &__preview-summary {
    display: flex;
    gap: 20px;
    font-size: 0.95rem;
    > span {
      font-weight: 500;
    }
    .text-erro {
      color: $status--red;
    }
    .text-alerta {
      color: #a67200;
    }
  }
  > div {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
}
</style>
